import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Lab-test catalogue client for Create Rx's "Advised investigations" card.
 *
 * Two stores sit behind these calls and they are NOT interchangeable:
 *  - the MySQL catalogue (`search`, `children`, `childrenBulk`, `info`) — the
 *    canonical panels and sub-tests, and the source of every code we save.
 *  - the Mongo synonym map (`synonyms`, `unitFor`) — hand-seeded aliases that
 *    additionally carry the parent panel and the unit's REFLD code. Partial by
 *    construction, so it enriches the catalogue and never replaces it.
 *
 * Empty responses differ per route and each is normalised to `[]`/`{}` here so
 * no caller has to know: the catalogue's sub-test routes answer 204 with no
 * body, the synonym routes answer 200 with the body `null`.
 */
class LabTestService {
  constructor() {
    this.apiService = new APIService();
  }

  /**
   * Fuzzy panel search. Answers `{label, value}` where `value` is the LABT code
   * and `label` is the catalogue's title-case panel name. Matches panel names
   * ONLY — a sub-test's name will not surface its panel here; that is what
   * `synonyms` is for.
   *
   * Encoded, unlike bridge — its `String.format` is a raw substitution, so a
   * query with an `&` or a space silently truncates the term server-side.
   */
  search(queryString, onResponse, onError) {
    const q = encodeURIComponent((queryString || "").trim());
    if (!q) {
      onResponse([]);
      return undefined;
    }
    return this.apiService.get(
      APIConfig.labTest.search.format(q),
      (res) => onResponse(Array.isArray(res) ? res : (res && res.data) || []),
      onError
    );
  }

  /**
   * One panel's sub-tests matching `queryString` — `[{label, value:"SLABT107"}]`.
   *
   * Kept for a targeted query; `children` is what the popover uses. A no-match
   * query comes back as an EMPTY BODY rather than `[]`.
   */
  subTests(ltCode, queryString, onResponse, onError) {
    const q = encodeURIComponent((queryString || "").trim());
    if (!ltCode || !q) {
      onResponse([]);
      return undefined;
    }
    return this.apiService.get(
      APIConfig.labTest.subTests.format(encodeURIComponent(ltCode), q),
      (res) => onResponse(Array.isArray(res) ? res : []),
      () => onResponse([])
    );
  }

  /**
   * A panel's FULL child list, in catalogue order. Rows are the rich
   * `SubLabTestResponse`: `{subtestName, sltcode, ltCode, loincCode,
   * abbreviatedName, …}` — note `sltcode` all-lowercase on this route.
   *
   * Never read `typeOfTest` off one of these: the service's transformer sets it
   * to the sub-test's own name, so it looks meaningful and is not.
   */
  children(ltCode, onResponse, onError) {
    if (!ltCode) {
      onResponse([]);
      return undefined;
    }
    return this.apiService.get(
      APIConfig.labTest.children.format(encodeURIComponent(ltCode)),
      (res) => onResponse(Array.isArray(res) ? res : []),
      () => onResponse([])
    );
  }

  /**
   * Children for several panels at once. Answers
   * `{ltCode: {name, tests:[{label, value}]}}` — the panel's CANONICAL name
   * alongside its children, because the synonym map's `mainTestName` cannot be
   * trusted for display (LABT284 is "X-Ray Ankle Lateral" in the catalogue and
   * "Hemoglobin electrophrosesis" in the synonym map; the catalogue wins).
   *
   * Three wire quirks are absorbed here so no caller has to know them: this
   * route nests the code as `sltCode` where the single-panel route spells it
   * `sltcode`; the child's name is `subtestName` (lowercase t) where the
   * single-panel route uses `subtestName` too but the panel's own is `testName`;
   * and an unknown code is simply absent from the response rather than an error.
   *
   * `postAgain` rather than `post`: `post` re-issues the request on a 401 and
   * would double-fire a catalogue read behind a token refresh.
   */
  childrenBulk(ltCodes, onResponse) {
    const codes = (ltCodes || []).filter(Boolean);
    if (!codes.length) {
      onResponse({});
      return undefined;
    }
    return this.apiService.postAgain(
      APIConfig.labTest.childrenBulk,
      { ltCodes: codes },
      (res) => {
        const byPanel = {};
        (Array.isArray(res) ? res : []).forEach((p) => {
          if (!p || !p.ltCode) return;
          byPanel[p.ltCode] = {
            name: p.testName || "",
            tests: (p.subtest || [])
              .filter((s) => s && (s.sltCode || s.sltcode))
              .map((s) => ({
                label: s.subtestName || s.subTestName || "",
                value: s.sltCode || s.sltcode,
                // Carried for de-duplication only: the catalogue holds the same
                // test under two codes in at least one panel.
                loinc: s.loincCode || "",
              })),
          };
        });
        onResponse(byPanel);
      },
      () => onResponse({})
    );
  }

  /**
   * Synonym search — the REVERSE lookup. `[{label, value:"SLABT107",
   * mainTestCode:"LABT31", mainTestName:"COMPLETE BLOOD COUNT", unit:"g/dL",
   * unitRef:"REFLD157"}]`.
   *
   * `label` is the ALIAS that matched, not the canonical test name, so it is
   * only safe to display after being resolved against the catalogue's own child
   * list. Several rows share one `value` (one row per alias) — dedupe.
   *
   * Failure is swallowed: this only ever enriches a search that already
   * succeeded, and a dead synonym map must not empty the popover.
   */
  synonyms(queryString, onResponse) {
    const q = encodeURIComponent((queryString || "").trim());
    if (!q) {
      onResponse([]);
      return undefined;
    }
    return this.apiService.get(
      APIConfig.labTestMapping.search.format(q),
      (res) => onResponse(Array.isArray(res) ? res : []),
      () => onResponse([])
    );
  }

  /**
   * The unit for one sub-test, as `{unit, unitRef}` — `unitRef` being the REFLD
   * code the prescription payload wants.
   *
   * Answers `{}` when the test has no seeded row, which is the common case: the
   * map is hand-maintained. Treat `unit: ""` as absent — the server's own
   * filter is `$exists`, which an empty string satisfies.
   */
  unitFor(sltCode, onResponse) {
    if (!sltCode) {
      onResponse({});
      return undefined;
    }
    return this.apiService.get(
      APIConfig.labTestMapping.bySubTest.format(encodeURIComponent(sltCode)),
      (res) => {
        const hit = (Array.isArray(res) ? res : []).find((r) => r && String(r.unit || "").trim());
        onResponse(hit ? { unit: String(hit.unit).trim(), unitRef: hit.unitRef || "" } : {});
      },
      () => onResponse({})
    );
  }

  /**
   * The catalogue row for one LABT code. Only `.instruction` is used — the
   * default advice bridge prefills when a test is picked, which the doctor can
   * then edit. A failure is not fatal: the row just starts with no instruction.
   */
  info(code, onResponse, onError) {
    if (!code) {
      onResponse({});
      return undefined;
    }
    return this.apiService.get(
      APIConfig.labTest.info.format(encodeURIComponent(code)),
      (res) => onResponse(res || {}),
      onError
    );
  }
}

export default LabTestService;
